// src/pages/AdoptPage.tsx
// おむかえマーケットページ

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon, HomeIcon } from '@heroicons/react/24/outline';
import { listCharactersForSale, CharacterForSale, formatPrice } from '../services/adoptionService';
import UserAvatar from '../components/common/UserAvatar';

const AdoptPage: React.FC = () => {
  const { t } = useTranslation('adopt');
  const [characters, setCharacters] = useState<CharacterForSale[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Fetch characters for sale
  const fetchCharacters = useCallback(async (search?: string, before?: string) => {
    if (before) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const response = await listCharactersForSale({
        limit: 20,
        search,
        before,
      });

      if (before) {
        setCharacters((prev) => [...prev, ...response.characters]);
      } else {
        setCharacters(response.characters);
      }
      setHasMore(response.hasMore);
    } catch (err) {
      console.error('Failed to fetch characters:', err);
      setError(t('errors.fetchFailed'));
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchCharacters();
  }, [fetchCharacters]);

  // Handle search
  const handleSearch = () => {
    fetchCharacters(searchQuery || undefined);
  };

  // Handle load more
  const handleLoadMore = () => {
    if (isLoadingMore || !hasMore) return;
    const lastChar = characters[characters.length - 1];
    fetchCharacters(searchQuery || undefined, lastChar?.createdAt);
  };

  return (
    <div className="min-h-screen bg-[#f8f7f8] dark:bg-gray-900">
      {/* Page Title */}
      <div className="max-w-6xl mx-auto px-4 pt-6 pb-4">
        <div className="flex items-center gap-2 mb-4">
          <HomeIcon className="w-6 h-6 text-pink-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">{t('title')}</h1>
        </div>

        {/* Search */}
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('search.placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 outline-none focus:border-purple-500 dark:focus:border-purple-400 transition-colors"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-5 py-2.5 bg-purple-500 text-white text-sm font-medium rounded-xl hover:bg-purple-600 transition-colors"
          >
            {t('search.button')}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 pb-8">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="p-4 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm rounded-xl">
            {error}
          </div>
        ) : characters.length === 0 ? (
          <div className="text-center py-12">
            <HomeIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              {t('empty')}
            </p>
          </div>
        ) : (
          <>
            {/* Character Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {characters.map((char) => (
                <Link
                  key={char.id}
                  to={`/character/${char.id}`}
                  className="group"
                >
                  <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                    {/* Thumbnail */}
                    <div className="aspect-square bg-gray-100 dark:bg-gray-700 relative overflow-hidden">
                      {char.thumbnailUrl ? (
                        <img
                          src={char.thumbnailUrl}
                          alt={char.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <HomeIcon className="w-12 h-12 text-gray-300 dark:text-gray-600" />
                        </div>
                      )}
                      {/* Price Badge */}
                      <div className="absolute top-2 right-2 px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full shadow-lg">
                        {formatPrice(char.salePrice)}
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-3">
                      <h3 className="font-medium text-gray-900 dark:text-white text-sm truncate">
                        {char.name}
                      </h3>
                      {/* Creator */}
                      <div className="flex items-center gap-1.5 mt-2">
                        <UserAvatar
                          src={char.creatorAvatar}
                          alt={char.creatorName || ''}
                          size="xs"
                          className="w-4 h-4"
                          fallbackInitial={char.creatorName?.charAt(0)}
                        />
                        <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {t('creator')}: @{char.creatorUsername}
                        </span>
                      </div>
                      {/* Owner (if different from creator) */}
                      {char.creatorId !== char.ownerId && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <UserAvatar
                            src={char.ownerAvatar}
                            alt={char.ownerName || ''}
                            size="xs"
                            className="w-4 h-4"
                            fallbackInitial={char.ownerName?.charAt(0)}
                          />
                          <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
                            {t('seller')}: @{char.ownerUsername}
                          </span>
                        </div>
                      )}
                      {/* Tags */}
                      {char.tags && char.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {char.tags.slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="text-[10px] text-gray-400 dark:text-gray-500"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className="px-6 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  {isLoadingMore ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                      {t('loading')}
                    </span>
                  ) : (
                    t('loadMore')
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdoptPage;
